/**
 * Round-4 U7 (E3) — the single definition of "this flow has unsaved work",
 * DOM-free and React-free so the bun harness under
 * `mini-apps/playbooks/__tests__` can cover it (same constraint
 * `play-flow-context.ts` and `usage-sidebar-logic.ts` document for
 * themselves: this app has no jsdom/happy-dom setup).
 *
 * Three dirty sources, deliberately not four:
 *
 *  1. `sequence-copy-draft-store.ts` — AN ENTRY EXISTS IF AND ONLY IF THE
 *     STEP IS DIRTY (that store's own stated invariant), so entry COUNT is
 *     the dirty signal; no per-entry comparison against a head is needed or
 *     wanted here.
 *  2. `generated-copy-store.ts` — only a `'ready'` entry counts. A
 *     `'pending'` generation has nothing the rep authored to lose (the run
 *     is in flight and is abandoned on navigation either way), and an
 *     `'error'` entry's only forward path is Discard/retry. Warning about
 *     either would make the dialog fire on navigation the rep has no reason
 *     to reconsider.
 *  3. `extraDirty` — the escape hatch for dirt that is NOT in a module-scope
 *     store. Today that is exactly `PlaybooksConfigPage`'s
 *     `dirtySegmentationIds` (component-local `useState`), passed in as a
 *     boolean rather than lifted into a store (round-4 Decision 12: a boolean
 *     prop, no store lift). Its consequence is stated, not hidden: Config's
 *     condition drafts still die on an unmount, so the guard there covers
 *     link clicks and tab close, not Back.
 *
 * `playbook-draft-store.ts` is deliberately ABSENT: it is write-through
 * (`persistLadder`), so a staged ladder is already on the server and is not
 * unsaved work.
 */

import type { GeneratedCopyEntry } from './generated-copy-store';
import type { CopyDraftValues } from './sequence-copy-draft-store';

export interface UnsavedWorkInput {
  /** `sequence-copy-draft-store`'s `entries` — dirty steps only. */
  copyDraftEntries: Record<string, CopyDraftValues>;
  /** `generated-copy-store`'s `entries` — every generation, in every status. */
  generatedEntries: Record<string, GeneratedCopyEntry>;
  /** Dirt held outside any store by the mounting surface. Omitted reads as clean. */
  extraDirty?: boolean;
}

export function hasUnsavedWork(input: UnsavedWorkInput): boolean {
  if (input.extraDirty === true) return true;
  if (Object.keys(input.copyDraftEntries).length > 0) return true;
  return Object.values(input.generatedEntries).some((entry) => entry.status === 'ready');
}

/**
 * Boolean store selectors — the subscription shape `useHasUnsavedWork`
 * (`components/UnsavedWorkGuard.tsx`) uses, so a keystroke that mutates one
 * draft entry's text never re-renders the guard or the header pill: the
 * selector's output only changes when the map crosses empty/non-empty.
 * Structurally typed on `entries` alone because neither store exports its
 * own state interface.
 */
export function selectHasCopyDrafts(state: { entries: Record<string, CopyDraftValues> }): boolean {
  return Object.keys(state.entries).length > 0;
}

export function selectHasReadyGeneratedCopy(state: {
  entries: Record<string, GeneratedCopyEntry>;
}): boolean {
  return Object.values(state.entries).some((entry) => entry.status === 'ready');
}

/**
 * R5-3 (playbooks-qa-round5-feedback.md) — the SCOPED twin of
 * {@link selectHasCopyDrafts}, for a host (Config) that must only ever read
 * ITS OWN editing surface's dirt, never every rung's.
 *
 * Before this fix, `PlaybooksConfigPage`'s `useHasUnsavedWork()` read the
 * WHOLE store: any leftover `rung:*`/`new:*` draft entry left alive by the
 * Play flow's deliberate Back-button draft survival (see
 * `UnsavedWorkGuard.tsx`'s module doc) — or by `SequenceCopyPane`'s own
 * "Save as new version" not clearing its `library|<seqId>|<pos>` entries
 * (the other half of this fix, see `SequenceCopyPane.tsx`) — made Config
 * read dirty for a page the rep never touched.
 *
 * Prefix-matches on `${scope}|`, the same key-format convention every other
 * scoped selector in `sequence-copy-draft-store.ts` uses (that store's own
 * `selectCopyDraftEntriesFor`, `migrateCopyDraftScope`, etc.) — `copyDraftKey`
 * is `${scope}|${sequenceId}|${position}`, so this prefix isolates exactly
 * one scope's entries regardless of which sequence/position they're under.
 *
 * Returns a plain boolean (never a fresh object), so a keystroke inside an
 * already-dirty step never re-renders whatever mounted this selector — same
 * contract {@link selectHasCopyDrafts} already documents for itself.
 */
export function selectHasCopyDraftsInScope(
  scope: string,
): (state: { entries: Record<string, CopyDraftValues> }) => boolean {
  const prefix = `${scope}|`;
  return (state) => Object.keys(state.entries).some((key) => key.startsWith(prefix));
}

/**
 * R5-3 — the scoped twin of {@link selectHasReadyGeneratedCopy}. `entries`
 * here are keyed `${scope}|${sequenceId}` (`generatedCopyKey`), so the same
 * `${scope}|` prefix isolates one editing surface's own generations.
 */
export function selectHasReadyGeneratedCopyInScope(
  scope: string,
): (state: { entries: Record<string, GeneratedCopyEntry> }) => boolean {
  const prefix = `${scope}|`;
  return (state) =>
    Object.entries(state.entries).some(([key, entry]) => key.startsWith(prefix) && entry.status === 'ready');
}
